import React, { useState, KeyboardEvent } from 'react';
import { TextField, IconButton, Box } from '@mui/material';
import { ArrowUpward, Stop } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import './ChatInput.scss';

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** When set, the send button becomes a stop button that calls this. */
  onStop?: () => void;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  disabled = false,
  placeholder,
  onStop,
}) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
    }
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <Box className="chat-input">
      <TextField
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder={placeholder ?? t('task.ai.input.typeMessage')}
        // Keep the field editable while planning/replying — `disabled` only
        // blocks send (button + Enter), so users can draft the next message.
        multiline
        maxRows={4}
        variant="outlined"
        className="chat-input__field"
        InputProps={{
          endAdornment: (
            <IconButton
              onClick={onStop ?? handleSend}
              disabled={onStop ? false : !message.trim() || disabled}
              aria-label={onStop ? t('task.ai.input.stop') : t('task.ai.input.send')}
              data-testid={onStop ? 'ai-chat-stop' : 'ai-chat-send'}
              className="chat-input__send-button"
            >
              {onStop ? (
                <Stop className="chat-input__send-button__icon" />
              ) : (
                <ArrowUpward className="chat-input__send-button__icon" />
              )}
            </IconButton>
          ),
        }}
      />
    </Box>
  );
};

export default ChatInput;
